import {
  type DemoAppointment,
  type DemoAppointmentStatus,
  PATIENT_CANCELLATION_REASON_STAFF_LABELS,
  SCHEDULING_PATIENT_CHANGE_STAFF_LABELS,
} from "@emr/contracts";
import { formatInClinic } from "./scheduleTime";

/** A booking's status as staff read it on its own (the status buttons and saved messages). */
export const APPOINTMENT_STATUS_LABELS: Readonly<Record<DemoAppointmentStatus, string>> = {
  booked: "Booked",
  confirmed: "Confirmed",
  checked_in: "Checked in",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

/**
 * The status chip on Schedule and Today (PA-9, lead decision L8): "Confirmed by patient" or
 * "Cancelled by patient" while the patient's own change from their manage link is still the
 * booking's status (the store drops `patientChange` once staff change the status or the time),
 * otherwise the plain status. It is the status chip, never a second pill.
 */
export function appointmentStatusLabel(
  appointment: Pick<DemoAppointment, "status" | "patientChange">,
): string {
  return appointment.patientChange === undefined
    ? APPOINTMENT_STATUS_LABELS[appointment.status]
    : SCHEDULING_PATIENT_CHANGE_STAFF_LABELS[appointment.patientChange.kind];
}

/**
 * One line under the chip for staff: when the patient confirmed or cancelled online, on the
 * clinic's clock, and the reason they chose for a cancellation. Null without a patient change.
 */
export function patientChangeDetail(
  appointment: Pick<DemoAppointment, "patientChange">,
  timeZone: string,
): string | null {
  const change = appointment.patientChange;
  if (change === undefined) return null;
  const when = formatInClinic(change.at, timeZone, { dateStyle: "medium", timeStyle: "short" });
  const verb = change.kind === "confirmed" ? "Confirmed" : "Cancelled";
  // "8:10 a.m." already ends the sentence: never "a.m..".
  const sentence = `${verb} online ${when}${when.endsWith(".") ? "" : "."}`;
  return change.cancellationReason === undefined
    ? sentence
    : `${sentence} Reason: ${PATIENT_CANCELLATION_REASON_STAFF_LABELS[change.cancellationReason]}.`;
}
